import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Modal,
  Grid,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";

function getCSRFToken() {
  const name = "csrftoken";
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

export default function AdminVariant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openListModal, setOpenListModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [currentLic, setCurrentLic] = useState(null);
  const [licensesInModal, setLicensesInModal] = useState([]);
  const [fields, setFields] = useState({
    email: "",
    password: "",
    code: "",
    estado: "disponible",
  });
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/productos/ver-productos/", { credentials: "include" });
        const { products } = await res.json();
        const list = products
          .filter((p) => p.email_licenses.length || p.code_licenses.length)
          .map((p) => ({
            product: p,
            licenses: [...p.email_licenses, ...p.code_licenses],
          }));
        setEntries(list);
      } catch {
        setErrorMsg("No se pudieron cargar licencias");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = entries.filter(
    ({ product }) =>
      (product.name || "").toLowerCase().includes((search || "").toLowerCase()) ||
      (product.code || "").toLowerCase().includes((search || "").toLowerCase())
  );

  const openLicenses = (entry) => {
    setCurrentEntry(entry);
    setLicensesInModal(entry.licenses || []);
    setOpenListModal(true);
  };

  const openEdit = (lic) => {
    setCurrentLic(lic);
    setFields({
      email: lic.email || "",
      password: lic.password || "",
      code: lic.code || "",
      estado: lic.estado || "disponible",
    });
    setOpenEditModal(true);
  };

  const handleField = (e) => {
    const { name, value } = e.target;
    setFields((f) => ({ ...f, [name]: value }));
  };

  const saveEdit = async () => {
    try {
      const csrf = getCSRFToken();
      const fd = new FormData();
      if (fields.email) fd.append("email", fields.email);
      if (fields.password) fd.append("password", fields.password);
      if (fields.code) fd.append("code", fields.code);
      fd.append("estado", fields.estado);

      const endpoint =
        fields.code && !fields.email
          ? `/productos/actualizar-licencia-codigo/${currentLic.id}/`
          : `/productos/actualizar-licencia-email/${currentLic.id}/`;

      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrf },
        body: fd,
      });

      if (!res.ok) throw new Error("Error al actualizar");

      const updated = {
        ...currentLic,
        email: fields.email || null,
        password: fields.password || null,
        code: fields.code || null,
        estado: fields.estado,
        stock: fields.estado === "disponible" ? 1 : 0,
      };

      setLicensesInModal((ls) => ls.map((l) => (l.id === updated.id ? updated : l)));
      setEntries((es) =>
        es.map((en) =>
          en.product.id === currentEntry.product.id
            ? {
                ...en,
                licenses: en.licenses.map((l) => (l.id === updated.id ? updated : l)),
              }
            : en
        )
      );

      setSuccessMsg("Licencia actualizada");
      setOpenEditModal(false);
    } catch {
      setErrorMsg("Error al actualizar");
    }
  };

  const deleteLicense = async (lic) => {
    try {
      const csrf = getCSRFToken();
      const endpoint =
        lic.code && !lic.email
          ? `/productos/eliminar-licencia-codigo/${lic.id}/`
          : `/productos/eliminar-licencia-email/${lic.id}/`;

      const res = await fetch(endpoint, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-CSRFToken": csrf },
      });

      if (!res.ok) throw Error();

      setLicensesInModal((ls) => ls.filter((l) => l.id !== lic.id));
      setEntries((es) =>
        es.map((en) =>
          en.product.id === currentEntry.product.id
            ? { ...en, licenses: en.licenses.filter((l) => l.id !== lic.id) }
            : en
        )
      );

      setSuccessMsg("Licencia eliminada");
    } catch {
      setErrorMsg("Error al eliminar");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />
      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />
        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
              onClick={() => navigate("nueva")}
            >
              Nueva Licencia
            </Button>
          </Box>

          <Paper sx={{ p: 3, borderRadius: "12px", bgcolor: "#FFFFFF" }}>
            <Typography variant="h6" mb={2}>
              Licencias de Productos
            </Typography>

            <TextField
              placeholder="Buscar producto..."
              variant="outlined"
              fullWidth
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {loading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Código</TableCell>
                      <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Producto</TableCell>
                      <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}># Licencias</TableCell>
                      <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Acción</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filtered
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((en) => {
                        const p = en.product;
                        return (
                          <TableRow key={p.id}>
                            <TableCell>{p.code}</TableCell>
                            <TableCell>{p.name}</TableCell>
                            <TableCell>{en.licenses.length}</TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => openLicenses(en)}
                                sx={{
                                  textTransform: "none",
                                  borderColor: "#1565C0",
                                  color: "#1565C0",
                                  "&:hover": { borderColor: "#0D47A1", color: "#0D47A1" },
                                }}
                              >
                                Ver Licencias
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>

                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
              </TableContainer>
            )}
          </Paper>

          <Modal open={openListModal} onClose={() => setOpenListModal(false)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "#FFFFFF",
                p: 3,
                borderRadius: "12px",
                width: 700,
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              <Typography variant="h6" mb={2}>
                Licencias de {currentEntry?.product.name}
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Email</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Password</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Code</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Estado</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Acción</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {licensesInModal.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.email || "-"}</TableCell>
                      <TableCell>{l.password || "-"}</TableCell>
                      <TableCell>{l.code || "-"}</TableCell>
                      <TableCell>{l.estado}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openEdit(l)} sx={{ color: "#1565C0" }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteLicense(l)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ textAlign: "right" }}>
                <Button onClick={() => setOpenListModal(false)}>Cerrar</Button>
              </Box>
            </Box>
          </Modal>

          <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "#FFFFFF",
                p: 3,
                borderRadius: "12px",
                width: 400,
              }}
            >
              <Typography variant="h6" mb={2}>
                Editar Licencia
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Email" name="email" value={fields.email} onChange={handleField} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Password" name="password" value={fields.password} onChange={handleField} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Code" name="code" value={fields.code} onChange={handleField} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Estado" name="estado" value={fields.estado} onChange={handleField} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ textAlign: "right" }}>
                <Button onClick={() => setOpenEditModal(false)} sx={{ mr: 1 }}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
                  onClick={saveEdit}
                >
                  Guardar Cambios
                </Button>
              </Box>
            </Box>
          </Modal>

          <Snackbar
            open={!!successMsg}
            autoHideDuration={3000}
            onClose={() => setSuccessMsg("")}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="success">{successMsg}</Alert>
          </Snackbar>

          <Snackbar
            open={!!errorMsg}
            autoHideDuration={3000}
            onClose={() => setErrorMsg("")}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="error">{errorMsg}</Alert>
          </Snackbar>
        </Box>
      </Box>
    </Box>
  );
}
